/**
 * Persisted UI preferences (Zustand + persist middleware).
 *
 * Single localStorage key (`gudwin.uiPrefs`) for cosmetic state so
 * we don't scatter per-feature keys. Currently:
 *  - `sidebarCollapsed` — LeftRail collapse state.
 *  - `ganttDensity` — Gantt row density across all Gantt screens
 *    (Project / Portfolio / Template / ApprovalDiff). See §7.3.
 *
 * Не персистим: `showTodayLine` — эфемерный per-mount toggle.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type GanttDensity = "compact" | "normal";

interface UiPrefsState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (v: boolean) => void;
  ganttDensity: GanttDensity;
  setGanttDensity: (v: GanttDensity) => void;
}

export const useUiPrefs = create<UiPrefsState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),
      ganttDensity: "normal",
      setGanttDensity: (v) => set({ ganttDensity: v }),
    }),
    {
      name: "gudwin.uiPrefs",
      version: 2,
      // v1 → v2: добавили `ganttDensity`. Persist middleware при отсутствии
      // ключа просто использует initial state, миграционная функция не нужна
      // — оставляем noop для явности контракта.
      migrate: (state) => state as UiPrefsState,
    },
  ),
);
